import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Settings,
  Key,
  Users,
  Plus,
  Send,
  Loader2,
  Eye,
  EyeOff,
  Shield,
  Trash2,
  AlertTriangle,
  Camera,
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { StaggerList, StaggerItem } from '../../components/animations/StaggerList';
import { useAuthStore } from '../../stores/authStore';
import { api } from '../../lib/api';
import toast from 'react-hot-toast';

interface AdminUser {
  id: string;
  name: string;
  email: string;
  avatarUrl: string | null;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export function SettingsPage() {
  const { user: currentUser, setUser } = useAuthStore();
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  // Password change state
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Create admin state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newAdminName, setNewAdminName] = useState('');
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [sendInvite, setSendInvite] = useState(true);
  const [isCreating, setIsCreating] = useState(false);

  // Delete admin state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<AdminUser | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchAdminUsers();
  }, []);

  const fetchAdminUsers = async () => {
    try {
      setIsLoading(true);
      const response = await api.get<AdminUser[]>('/users?role=admin');
      setAdminUsers(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to load admin users:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setIsUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.upload('/uploads/avatar', formData);
      const data = response.data as { avatarUrl: string };

      // Update the auth store with new avatar
      if (currentUser) {
        setUser({ ...currentUser, avatarUrl: data.avatarUrl });
      }
      toast.success('Avatar updated successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to upload avatar');
    } finally {
      setIsUploadingAvatar(false);
      if (avatarInputRef.current) {
        avatarInputRef.current.value = '';
      }
    }
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setIsChangingPassword(true);
    try {
      await api.post(`/users/${currentUser?.id}/reset-password`, {
        password: newPassword,
      });
      toast.success('Password changed successfully');
      resetPasswordModal();
    } catch (err: any) {
      toast.error(err.message || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleCreateAdmin = async () => {
    if (!newAdminName.trim() || !newAdminEmail.trim()) {
      toast.error('Please enter name and email');
      return;
    }

    if (!sendInvite && newAdminPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setIsCreating(true);
    try {
      await api.post('/users', {
        name: newAdminName.trim(),
        email: newAdminEmail.trim(),
        role: 'admin',
        password: sendInvite ? undefined : newAdminPassword,
        sendInvite: sendInvite,
      });
      toast.success(
        sendInvite
          ? `Invitation sent to ${newAdminEmail}`
          : 'Admin user created successfully'
      );
      resetCreateModal();
      fetchAdminUsers();
    } catch (err: any) {
      toast.error(err.message || 'Failed to create admin user');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteAdmin = async () => {
    if (!userToDelete) return;

    setIsDeleting(true);
    try {
      await api.delete(`/users/${userToDelete.id}`);
      toast.success('Admin user deactivated');
      resetDeleteModal();
      fetchAdminUsers();
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete admin user');
    } finally {
      setIsDeleting(false);
    }
  };

  const resetPasswordModal = () => {
    setNewPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setPasswordModalOpen(false);
  };

  const resetCreateModal = () => {
    setNewAdminName('');
    setNewAdminEmail('');
    setNewAdminPassword('');
    setSendInvite(true);
    setCreateModalOpen(false);
  };

  const resetDeleteModal = () => {
    setUserToDelete(null);
    setDeleteModalOpen(false);
  };

  const formatDate = (date: string | null) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-NZ', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <div className="p-2 rounded-xl bg-accent/10">
            <Settings className="w-8 h-8 text-accent" />
          </div>
          Settings
        </h1>
        <p className="text-text-secondary mt-1">
          Manage your account and admin users
        </p>
      </motion.div>

      {/* Hidden file input for avatar */}
      <input
        ref={avatarInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleAvatarUpload}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Account Security */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card hover={false}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5 text-accent" />
                Account Security
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4 p-4 rounded-xl bg-bg-tertiary">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={isUploadingAvatar}
                  className="relative w-16 h-16 rounded-full overflow-hidden group flex-shrink-0"
                >
                  {currentUser?.avatarUrl ? (
                    <img
                      src={currentUser.avatarUrl}
                      alt={currentUser.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-accent to-accent-light flex items-center justify-center text-white font-bold text-xl">
                      {currentUser?.name?.charAt(0) || '?'}
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    {isUploadingAvatar ? (
                      <Loader2 className="w-5 h-5 text-white animate-spin" />
                    ) : (
                      <Camera className="w-5 h-5 text-white" />
                    )}
                  </div>
                </motion.button>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{currentUser?.name}</p>
                  <p className="text-sm text-text-muted truncate">{currentUser?.email}</p>
                  <p className="text-xs text-text-muted mt-1">Click avatar to change</p>
                </div>
              </div>

              <Button
                onClick={() => setPasswordModalOpen(true)}
                variant="secondary"
                className="w-full"
              >
                <Key className="w-4 h-4" />
                Change Password
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        {/* Admin Users */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card hover={false}>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-accent" />
                Admin Users ({adminUsers.length})
              </CardTitle>
              <Button size="sm" onClick={() => setCreateModalOpen(true)}>
                <Plus className="w-4 h-4" />
                Add Admin
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="w-6 h-6 animate-spin text-accent" />
                </div>
              ) : adminUsers.length === 0 ? (
                <div className="p-6 text-center text-text-muted">
                  <Shield className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No admin users found</p>
                </div>
              ) : (
                <StaggerList className="divide-y divide-border max-h-80 overflow-y-auto">
                  {adminUsers.map((admin) => (
                    <StaggerItem
                      key={admin.id}
                      className="p-4 hover:bg-bg-tertiary/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent to-accent-light flex items-center justify-center text-white font-semibold">
                          {admin.name.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium truncate">{admin.name}</p>
                            {admin.id === currentUser?.id && (
                              <Badge size="sm" variant="accent">You</Badge>
                            )}
                            {!admin.isActive && (
                              <Badge size="sm" variant="danger">Inactive</Badge>
                            )}
                          </div>
                          <p className="text-sm text-text-muted truncate">
                            {admin.email}
                          </p>
                        </div>
                        {admin.id !== currentUser?.id && admin.isActive && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setUserToDelete(admin);
                              setDeleteModalOpen(true);
                            }}
                            className="text-text-muted hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-text-muted mt-2 ml-13">
                        Last login: {formatDate(admin.lastLoginAt)}
                      </p>
                    </StaggerItem>
                  ))}
                </StaggerList>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Change Password Modal */}
      <Modal
        isOpen={passwordModalOpen}
        onClose={resetPasswordModal}
        title="Change Password"
        description="Enter your new password"
      >
        <div className="space-y-4">
          <div className="relative">
            <Input
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-9 text-text-muted hover:text-text"
            >
              {showPassword ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          </div>
          <Input
            label="Confirm Password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
          <p className="text-sm text-text-muted">
            Password must be at least 8 characters long
          </p>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={resetPasswordModal}>
              Cancel
            </Button>
            <Button
              onClick={handleChangePassword}
              disabled={!newPassword || !confirmPassword || isChangingPassword}
              isLoading={isChangingPassword}
            >
              <Key className="w-4 h-4" />
              Change Password
            </Button>
          </div>
        </div>
      </Modal>

      {/* Create Admin Modal */}
      <Modal
        isOpen={createModalOpen}
        onClose={resetCreateModal}
        title="Add Admin User"
        description="Create a new admin account"
      >
        <div className="space-y-4">
          <Input
            label="Name"
            placeholder="John Smith"
            value={newAdminName}
            onChange={(e) => setNewAdminName(e.target.value)}
            required
          />
          <Input
            label="Email"
            type="email"
            placeholder="john@example.com"
            value={newAdminEmail}
            onChange={(e) => setNewAdminEmail(e.target.value)}
            required
          />

          <div className="flex items-center gap-3 p-3 rounded-xl bg-bg-tertiary">
            <input
              type="checkbox"
              id="sendInvite"
              checked={sendInvite}
              onChange={(e) => setSendInvite(e.target.checked)}
              className="w-4 h-4 rounded border-border accent-accent"
            />
            <label htmlFor="sendInvite" className="text-sm">
              Send welcome email with login link
            </label>
          </div>

          {!sendInvite && (
            <Input
              label="Password"
              type="password"
              placeholder="Enter password"
              value={newAdminPassword}
              onChange={(e) => setNewAdminPassword(e.target.value)}
              required
            />
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={resetCreateModal}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateAdmin}
              disabled={
                !newAdminName.trim() ||
                !newAdminEmail.trim() ||
                (!sendInvite && newAdminPassword.length < 8) ||
                isCreating
              }
              isLoading={isCreating}
            >
              {sendInvite ? (
                <>
                  <Send className="w-4 h-4" />
                  Send Invitation
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Create Admin
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Admin Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={resetDeleteModal}
        title="Deactivate Admin User"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-500">Are you sure?</p>
              <p className="text-sm text-text-secondary mt-1">
                This will deactivate{' '}
                <span className="font-semibold">{userToDelete?.name}</span>'s admin
                account. They will no longer be able to access the admin portal.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={resetDeleteModal}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteAdmin}
              disabled={isDeleting}
              isLoading={isDeleting}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              <Trash2 className="w-4 h-4" />
              Deactivate User
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
